/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, Dialog, DialogContent, FormControl, OutlinedInput, Tooltip } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { InfoCircleOutlined } from "@ant-design/icons";
import { capitalize } from "lodash";
import { Loader } from "../loader";
import { LoadingSpinner } from "../loading_spinner";
import { ModalCancelButton } from "../modal_cancel_button";
import { SelectedMenuItem } from "../selected_menu_item";
import { StyledMenuItem } from "../styled_menu_item";
import { InstanceRoleSection } from "./instance_role_section";
import { ModuleAccessSection } from "./module_access_section";
import {
  StyledChip,
  StyledHelperText,
  StyledInputLabel,
  StyledPhoneField,
  StyledSelect,
  StyledTextField,
} from "../../pages/user_management/styles";
import {
  AdminDialogPaperProps,
  CloseIconButton,
  FieldContainer,
  FieldLabel,
  FixedActionContainer,
  LoadingContainer,
  RequiredMark,
  SaveButton,
  scrollableContent,
  SectionContainer,
  TitleContainer,
  TitleText,
  topLevelMenuProps,
  tooltipIconStyle,
} from "./styles";

export const TOP_LEVEL_ROLES = [
  { name: 'Admin', value: 'admin' },
  { name: 'Viewer', value: 'viewer' },
];

const IDENTITY_FIELDS = [
  { field: 'first_name', label: 'First Name', testId: 'first-name' },
  { field: 'last_name', label: 'Last Name', testId: 'last-name' },
  { field: 'username', label: 'Username', testId: 'username' },
  { field: 'phone_no', label: 'Mobile Phone', testId: 'phone', isPhone: true },
  { field: 'email', label: 'Email Address', testId: 'email' },
];

const MODE_COPY = {
  create: { title: 'Create User', submitLabel: 'Add', testIdPrefix: 'create' },
  edit: { title: 'Update User', submitLabel: 'Update', testIdPrefix: 'edit' },
};

const NO_VALUES = {};
const NO_ERRORS = {};

/**
 * Create / edit modal for a restaurant admin.
 *
 * Presentational: the page owns the form state and the mutation, and passes the outcome back in
 * through `isSubmitting` and `errors`. Both modes render from the same markup, so the two forms
 * cannot drift apart; only the copy, the required marks and which state is bound differ.
 *
 * @param {String}   mode          `create` or `edit`
 * @param {Boolean}  open          whether the modal is shown
 * @param {Boolean}  isLoading     the record is still being fetched, so show a loader in its place
 * @param {Boolean}  isSubmitting  a save is in flight
 * @param {Object}   values        the identity fields now in the form
 * @param {Object}   errors        field errors, from validation or the last rejected save
 * @param {Function} onFieldChange called with (value, field) as an identity field is edited
 * @param {Object}   moduleAccess  module access props, or null while the section does not apply
 */
export const RestaurantAdminModal = ({
  mode = 'create',
  open = false,
  isLoading = false,
  isSubmitting = false,
  values = NO_VALUES,
  errors = NO_ERRORS,
  onFieldChange,
  onClose,
  onSubmit,
  topLevelRoles = [],
  onTopLevelRolesChange,
  onTopLevelRoleDelete,
  instanceRoles = [],
  instanceHandlers,
  resources,
  getList,
  getInstanceName,
  moduleAccess = null,
}) => {
  const { title, submitLabel, testIdPrefix } = MODE_COPY[mode] || MODE_COPY.create;
  const isCreateMode = mode === 'create';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={AdminDialogPaperProps}
      data-testid={`${testIdPrefix}-modal`}
      aria-labelledby={`${testIdPrefix}-modal-title`}
    >
      <TitleContainer>
        <TitleText id={`${testIdPrefix}-modal-title`} data-testid={`${testIdPrefix}-modal-title`}>
          {title}
        </TitleText>
        <CloseIconButton
          data-testid={`${testIdPrefix}-modal-close-btn`}
          onClick={onClose}
          aria-label={`Close ${title.toLowerCase()} modal`}
          disableRipple
        >
          <ClearIcon fontSize="small" />
        </CloseIconButton>
      </TitleContainer>

      <DialogContent sx={scrollableContent}>
        {isLoading ? (
          <LoadingContainer data-testid={`${testIdPrefix}-modal-loader`}>
            <Loader />
          </LoadingContainer>
        ) : (
          <React.Fragment>
            <SectionContainer>
              {IDENTITY_FIELDS.map(({ field, label, testId, isPhone }) => (
                <FieldContainer key={field}>
                  <FieldLabel htmlFor={`${testIdPrefix}-${testId}-input`}>
                    {label}
                    {isCreateMode && <RequiredMark>*</RequiredMark>}
                  </FieldLabel>

                  {isPhone ? (
                    <React.Fragment>
                      <StyledPhoneField
                        value={values?.[field] || ''}
                        onChange={(phone) => onFieldChange?.(phone, field)}
                        defaultCountry="us"
                        forceDialCode
                        borderColor={errors?.[field] ? 'red' : '#c4c4c4'}
                        inputProps={{
                          id: `${testIdPrefix}-${testId}-input`,
                          'data-testid': `${testIdPrefix}-${testId}-input`,
                        }}
                      />
                      {!!errors?.[field] && (
                        <StyledHelperText data-testid={`${testIdPrefix}-${testId}-error`}>
                          {`${errors[field]}`}
                        </StyledHelperText>
                      )}
                    </React.Fragment>
                  ) : (
                    <StyledTextField
                      fullWidth
                      size="small"
                      id={`${testIdPrefix}-${testId}-input`}
                      value={values?.[field] || ''}
                      onChange={(e) => onFieldChange?.(e.target.value, field)}
                      error={!!errors?.[field]}
                      helperText={errors?.[field]}
                      autoComplete="off"
                      data-testid={`${testIdPrefix}-${testId}`}
                    />
                  )}
                </FieldContainer>
              ))}
            </SectionContainer>

            <SectionContainer>
              <FieldContainer>
                <FieldLabel as="div">
                  Select Top Level Role
                  <Tooltip title="Assign top level roles to the user" arrow placement="top">
                    <InfoCircleOutlined style={tooltipIconStyle} />
                  </Tooltip>
                </FieldLabel>

                <FormControl sx={{ width: '100%' }}>
                  <StyledInputLabel id="demo-multiple-chip-label">Roles</StyledInputLabel>
                  <StyledSelect
                    labelId="demo-multiple-chip-label"
                    id="demo-multiple-chip"
                    multiple
                    value={topLevelRoles}
                    onChange={onTopLevelRolesChange}
                    input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                    MenuProps={topLevelMenuProps}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, padding: '10px' }}>
                        {selected.map((value) => (
                          <StyledChip
                            key={value}
                            label={capitalize(value)}
                            onDelete={() => onTopLevelRoleDelete?.(value)}
                            onMouseDown={(event) => event.stopPropagation()}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {TOP_LEVEL_ROLES.map((role) => (
                      <StyledMenuItem key={role.value} value={role.value}>
                        {topLevelRoles?.includes(role.value)
                          ? <SelectedMenuItem data={role?.name} />
                          : role?.name}
                      </StyledMenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
              </FieldContainer>

              <FieldContainer>
                <FieldLabel as="div">
                  Select Instance Role
                  <Tooltip title="Assign instance level roles to the user" arrow placement="top">
                    <InfoCircleOutlined style={tooltipIconStyle} />
                  </Tooltip>
                </FieldLabel>

                <InstanceRoleSection
                  instanceRoles={instanceRoles}
                  handlers={instanceHandlers}
                  resources={resources}
                  getList={getList}
                  getInstanceName={getInstanceName}
                />
              </FieldContainer>
            </SectionContainer>

            {!!moduleAccess && (
              <SectionContainer data-testid={`${testIdPrefix}-module-access-section`}>
                <ModuleAccessSection moduleAccess={moduleAccess} testIdPrefix={testIdPrefix} />
              </SectionContainer>
            )}
          </React.Fragment>
        )}
      </DialogContent>

      <FixedActionContainer>
        <ModalCancelButton
          data-testid={`${testIdPrefix}-modal-cancel-btn`}
          onClick={onClose}
          disabled={isSubmitting}
          disableRipple
        >
          Cancel
        </ModalCancelButton>

        {!isLoading && (
          <SaveButton
            data-testid={`${testIdPrefix}-modal-save-btn`}
            variant="contained"
            onClick={onSubmit}
            disabled={isSubmitting}
            disableRipple
          >
            {isSubmitting ? <LoadingSpinner isLoading={true} /> : submitLabel}
          </SaveButton>
        )}
      </FixedActionContainer>
    </Dialog>
  );
};

export default RestaurantAdminModal;
